import axios from "axios";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");   // empty = same origin (never the text "undefined")
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

/*
 * Add the NAVI CARE JWT token to every authenticated request.
 */
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("nw_token");

  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  // Preserve QR/campaign attribution through login, registration and checkout redirects.
  try {
    const qrCode = localStorage.getItem("navi_qr_code");
    const campaign = localStorage.getItem("navi_qr_campaign");
    if (qrCode) cfg.headers["X-Navi-QR-Code"] = qrCode;
    if (campaign) cfg.headers["X-Navi-QR-Campaign"] = campaign;
  } catch {}

  return cfg;
});

/*
 * IMPORTANT:
 *
 * Do NOT automatically delete nw_token when any request returns 401.
 *
 * The application can have an old /auth/me request running while
 * a new login is taking place. Automatically deleting the token here
 * can erase a newly successful login.
 *
 * Authentication state is handled by auth.jsx instead.
 */


// Phones drop connections. A GET that failed on the network (or 502/503/504) is retried once after a short pause.
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config;
  const status = error?.response?.status;
  const transient = !error?.response || [502, 503, 504].includes(status);
  if (!cfg || String(cfg.method).toLowerCase() !== "get" || cfg.__retried || !transient || error?.code === "ERR_CANCELED") throw error;
  cfg.__retried = true;
  await new Promise((r) => setTimeout(r, 700));
  return api(cfg);
});

/** Resolve any CMS image value into a browser-loadable URL. Supports legacy strings and
 * structured media values with persisted crop/position settings. */
export function getMediaPresentation(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      src: value.src || "",
      fit: value.fit === "contain" ? "contain" : "cover",
      scale: Math.max(1, Math.min(2.5, Number(value.scale) || 1)),
      x: Math.max(0, Math.min(100, Number(value.x) || 50)),
      y: Math.max(0, Math.min(100, Number(value.y) || 50)),
      aspect: value.aspect || "auto",
    };
  }
  return { src: typeof value === "string" ? value : "", fit: "cover", scale: 1, x: 50, y: 50, aspect: "auto" };
}

export function resolveMediaUrl(value) {
  const media = getMediaPresentation(value);
  const raw = media.src;
  if (!raw || typeof raw !== "string") return "";
  const v = raw.trim();
  if (!v) return "";

  const driveId = extractGoogleDriveId(v);
  if (driveId) return `https://drive.google.com/thumbnail?id=${encodeURIComponent(driveId)}&sz=w2400`;
  if (/^(https?:|data:|blob:)/i.test(v)) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  if (v.startsWith("/")) return `${BACKEND_URL}${v}`;
  if (v.startsWith("navi-care/public/")) return `${BACKEND_URL}/api/public-files/${encodeURI(v)}`;
  return v;
}

export function mediaStyle(value, extra = {}) {
  const media = getMediaPresentation(value);
  return {
    objectFit: media.fit,
    objectPosition: `${media.x}% ${media.y}%`,
    transform: `scale(${media.scale})`,
    ...extra,
  };
}

export function extractGoogleDriveId(value) {
  if (!value || typeof value !== "string") return "";
  const v = value.trim();
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i,
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/i,
    /drive\.google\.com\/uc\?(?:[^#]*&)?id=([a-zA-Z0-9_-]+)/i,
    /docs\.google\.com\/uc\?(?:[^#]*&)?id=([a-zA-Z0-9_-]+)/i,
  ];
  for (const pattern of patterns) {
    const match = v.match(pattern);
    if (match?.[1]) return match[1];
  }
  return "";
}

// Calendar/service dates are date-only business values. Do not pass YYYY-MM-DD
// through new Date(), which can shift the displayed day in IST and other zones.
export function formatServiceDate(value, locale = "en-IN") {
  const raw = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return value || "—";
  const [y, m, d] = raw.split("-").map(Number);
  return new Intl.DateTimeFormat(locale || undefined, { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(y, m - 1, d));
}

export function todayServiceDate() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
}
